import { COURSE_BUILDER_REQUEST_TAB_ORGANIZATION_TYPE_ID } from "@features/CourseBuilder/config/course.builder.configs";

/** Organization type ID for PSDA Admin users (same as Course Builder Request tab). */
export const PSDA_ADMIN_ORGANIZATION_TYPE_ID = COURSE_BUILDER_REQUEST_TAB_ORGANIZATION_TYPE_ID;

/**
 * Prototype-only permissions injected for PSDA Admin.
 * Course Assign and Event Management now use real API permissions — keep empty unless a static prototype remains.
 */
export const STATIC_PROTOTYPE_PERMISSION_LIST = [] as const;

const normalizeToken = (value: string): string =>
	value.trim().toLowerCase().replace(/-/g, "_").replace(/\s+/g, "_");

/** API may return roles as strings or `{ name, identifier }` objects. */
export function extractRoleTokens(roles: unknown[] | undefined): string[] {
	if (!roles?.length) return [];

	return roles.flatMap(role => {
		if (typeof role === "string" && role.trim()) return [role.trim()];
		if (role && typeof role === "object") {
			const record = role as { name?: unknown; identifier?: unknown };
			const tokens: string[] = [];
			if (typeof record.name === "string" && record.name.trim()) tokens.push(record.name.trim());
			if (typeof record.identifier === "string" && record.identifier.trim()) {
				tokens.push(record.identifier.trim());
			}
			return tokens;
		}
		return [];
	});
}

export function isPsdaAdminByOrganizationType(organizationTypeId?: number | null): boolean {
	return Number(organizationTypeId) === PSDA_ADMIN_ORGANIZATION_TYPE_ID;
}

export function isPsdaAdminByRole(roles: unknown[]): boolean {
	return extractRoleTokens(roles).some(role => {
		const normalized = normalizeToken(role);
		return (
			normalized === "psda_admin" ||
			normalized === "psdaadmin" ||
			(normalized.includes("psda") && normalized.includes("admin"))
		);
	});
}

/** Exact role match after normalizing case, spaces, and hyphens. Handles string or `{ name, identifier }` roles. */
export function includesRole(roles: unknown[] | undefined, role: string): boolean {
	const target = normalizeToken(role);
	if (!target) return false;
	return extractRoleTokens(roles).some(token => normalizeToken(token) === target);
}

/**
 * True when the user has `role`, or is PSDA Admin.
 * Use on a single role gate. Do not apply this to every branch of a priority chain,
 * or PSDA Admin would always match the first branch.
 */
export function includesRoleOrPsdaAdmin(roles: unknown[] | undefined, role: string): boolean {
	return includesRole(roles, role) || isPsdaAdminByRole(roles ?? []);
}

export function isPsdaAdminByUserTypeName(userTypeName?: string | null): boolean {
	if (!userTypeName) return false;
	const normalized = normalizeToken(userTypeName);
	return normalized.includes("psda") && normalized.includes("admin");
}

export function shouldGrantStaticPrototypePermissions(options: {
	roles?: unknown[];
	organizationTypeId?: number | null;
	userTypeName?: string | null;
}): boolean {
	return (
		isPsdaAdminByOrganizationType(options.organizationTypeId) ||
		isPsdaAdminByRole(options.roles ?? []) ||
		isPsdaAdminByUserTypeName(options.userTypeName)
	);
}

export function withStaticPrototypePermissions(
	permissions: string[],
	options: {
		roles?: string[];
		organizationTypeId?: number | null;
		userTypeName?: string | null;
	},
): string[] {
	if (!shouldGrantStaticPrototypePermissions(options) || STATIC_PROTOTYPE_PERMISSION_LIST.length === 0) {
		return permissions;
	}
	return [...new Set([...permissions, ...STATIC_PROTOTYPE_PERMISSION_LIST])];
}

/** @deprecated Use withStaticPrototypePermissions from shared module. */
export const withEventManagementPrototypePermissions = withStaticPrototypePermissions;
